import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * EventLocationMap — shows event location on a map.
 * Two modes:
 *   - Obscured (locationRevealed === false): Zoom 13, non-interactive, dashed circle, city label
 *   - Exact (locationRevealed === true): Zoom 15, interactive, pin marker, full address
 */
export default function EventLocationMap({
  latitude,
  longitude,
  locationRevealed = false,
  locationAddress,
  locationCity,
  fundingType = 'party_starter',
  height = 192,
  hideCaption = false,
  bare = false,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!mapContainerRef.current || !latitude || !longitude) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [longitude, latitude],
      zoom: locationRevealed ? 15 : 13,
      interactive: locationRevealed,
      attributionControl: false,
    });

    mapRef.current = map;

    map.on('load', () => {
      if (locationRevealed) {
        // Exact mode — show a black pin marker
        const markerEl = document.createElement('div');
        markerEl.innerHTML = `
          <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="black"/>
            <circle cx="16" cy="16" r="6" fill="white"/>
          </svg>
        `;
        markerEl.style.cursor = 'pointer';

        new mapboxgl.Marker({ element: markerEl, anchor: 'bottom' })
          .setLngLat([longitude, latitude])
          .addTo(map);
      } else {
        // Obscured mode — show a dashed ~0.8km radius circle
        const radiusKm = 0.8;
        const center = [longitude, latitude];
        const points = 64;
        const coords = [];

        for (let i = 0; i < points; i++) {
          const angle = (i / points) * 2 * Math.PI;
          const dx = radiusKm / (111.32 * Math.cos((latitude * Math.PI) / 180));
          const dy = radiusKm / 110.574;
          coords.push([
            center[0] + dx * Math.cos(angle),
            center[1] + dy * Math.sin(angle),
          ]);
        }
        coords.push(coords[0]); // Close the ring

        map.addSource('obscured-area', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [coords],
            },
          },
        });

        // Fill
        map.addLayer({
          id: 'obscured-fill',
          type: 'fill',
          source: 'obscured-area',
          paint: {
            'fill-color': '#000000',
            'fill-opacity': 0.06,
          },
        });

        // Dashed outline
        map.addLayer({
          id: 'obscured-outline',
          type: 'line',
          source: 'obscured-area',
          paint: {
            'line-color': '#000000',
            'line-width': 2,
            'line-dasharray': [4, 4],
          },
        });
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, locationRevealed]);

  if (!latitude || !longitude) return null;

  return (
    <div className="w-full">
      <div
        ref={mapContainerRef}
        className={`w-full ${bare ? '' : 'border-2 border-black'}`}
        style={{ height, minHeight: height }}
        data-testid="event-location-map"
      />
      {!hideCaption && (
      <div className="border-2 border-t-0 border-black px-4 py-3 bg-white">
        {locationRevealed ? (
          <p className="font-mono text-sm text-black font-semibold">
            {locationAddress || 'Address not available'}
          </p>
        ) : (
          <p className="font-mono text-xs text-gray-600 uppercase tracking-wider">
            AREA: {locationCity || 'NEARBY'} — EXACT LOCATION REVEALED AFTER {fundingType === 'guaranteed' ? 'PURCHASE' : 'GREENLIGHT'}
          </p>
        )}
      </div>
      )}
    </div>
  );
}
